import React, { useState, memo, useMemo } from "react";
import { Box, Button, Chip, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AuthModal from "./AuthModal";
import { useWalletAuth } from "./WalletConnect";
import { useNavigate } from "react-router-dom";
import { useAccountUsdBalances } from "../appRWAPromo/hooks/useAccountUsdBalances"; // <-- добавили

const chipSx = {
  color: "#e5e7eb",
  borderColor: "rgba(255,255,255,.18)",
  bgcolor: "rgba(15,23,42,.35)",
  "& .MuiChip-label": { color: "#e5e7eb", fontWeight: 800 },
};

const glassButtonSx = {
  background:
    "linear-gradient(135deg, rgba(18, 22, 28, 0.64) 0%, rgba(10,13,17,.92) 100%)",
  color: "#e5e7eb",
  borderRadius: 10,
  textTransform: "none",
  fontWeight: 800,
  padding: "6px 14px",
  lineHeight: 1.2,
  boxShadow: "0 10px 28px rgba(2,8,23,.45)",
  border: "none",
  outline: "none",
  "&:hover": {
    background:
      "linear-gradient(135deg, rgba(22,26,33,.96) 0%, rgba(12,15,19,.96) 100%)",
    boxShadow: "0 12px 32px rgba(2,8,23,.55)",
  },
  "&:active": { transform: "translateY(1px)" },
  "&.Mui-disabled": { opacity: 0.6, color: "#9ca3af" },
};

function shortAddress(a) {
  if (!a) return "";
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function fmtUsd(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function AuthButtons() {
  const nav = useNavigate();

  const {
    address,
    isConnected,
    chainIdNum,
    isAuthed,
    fbLoading,
    mustVerifyEmail,
    isBusy,
    pendingVerify,
    startWalletAuth,
    connectWalletOnly,
    openAccount,
  } = useWalletAuth();

  // Балансы аккаунта из Firestore (реалтайм)
  const acc = useAccountUsdBalances();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("login");

  const openModal = (mode) => {
    setModalMode(mode);
    setModalOpen(true);
  };

  const onArrowClick = () => {
    if (isConnected) openAccount();
    else connectWalletOnly();
  };

  // удобно собрать строку в один Chip
  const totalChip = useMemo(() => {
    if (!isAuthed) return null;
    const label = acc.loading ? "TOTAL: Loading..." : `TOTAL: ${fmtUsd(acc.totalUsd)}`;
    return <Chip sx={chipSx} label={label} variant="outlined" />;
  }, [isAuthed, acc.loading, acc.totalUsd]);

  const availableChip = useMemo(() => {
    if (!isAuthed) return null;
    const label = acc.loading ? " AVAILABLE: Loading..." : ` ${fmtUsd(acc.availableUsd)}`;
    return <Chip sx={chipSx} label={label} variant="outlined" />;
  }, [isAuthed, acc.loading, acc.availableUsd]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Адрес — только если кошелек подключен */}
      {isConnected && address ? (
        <Chip sx={chipSx} label={shortAddress(address)} variant="outlined" />
      ) : null}

      {/* VERIFY EMAIL */}
      {mustVerifyEmail ? (
        <Button
          size="small"
          variant="contained"
          onClick={() =>
            nav("/verify-email", { replace: true, state: { next: "/market" } })
          }
          sx={{ ...glassButtonSx, fontFamily: "'Bruno Ace SC', sans-serif", letterSpacing: 1 }}
        >
          VERIFY EMAIL
        </Button>
      ) : null}

      {isAuthed ? (
        <>
          {/* ВМЕСТО кошельковых ETH/USDT показываем аккаунтные USD */}
          {totalChip}
          {availableChip}

          {/* CONNECT WALLET — только когда authed, но кошелек не подключен */}
          {!isConnected ? (
            <Button
              size="small"
              variant="contained"
              disabled={fbLoading || isBusy || pendingVerify || mustVerifyEmail}
              onClick={() => startWalletAuth()}
              sx={{ ...glassButtonSx, fontFamily: "'Bruno Ace SC', sans-serif", letterSpacing: 1 }}
            >
              CONNECT WALLET
            </Button>
          ) : null}

          {/* Arrow */}
          <Tooltip title={isConnected ? "Account" : "Connect wallet"} arrow>
            <IconButton
              onClick={onArrowClick}
              disabled={fbLoading || isBusy || pendingVerify || mustVerifyEmail}
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,.12)",
                bgcolor: "rgba(15,23,42,.55)",
                color: "#e5e7eb",
                boxShadow: "0 8px 24px rgba(2,8,23,.35)",
                "&:hover": { bgcolor: "rgba(15,23,42,.75)" },
                "&.Mui-disabled": { opacity: 0.5, color: "#9ca3af" },
              }}
              aria-label="Open account"
            >
              <KeyboardArrowDownRoundedIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            size="small"
            variant="contained"
            disabled={fbLoading || isBusy}
            onClick={() => openModal("login")}
            sx={{ ...glassButtonSx, fontFamily: "'Bruno Ace SC', sans-serif", letterSpacing: 1 }}
          >
            LOGIN
          </Button>

          <Button
            size="small"
            variant="contained"
            disabled={fbLoading || isBusy}
            onClick={() => openModal("signup")}
            sx={{ ...glassButtonSx, fontFamily: "'Bruno Ace SC', sans-serif", letterSpacing: 1 }}
          >
            SIGN UP
          </Button>

          <AuthModal
            open={modalOpen}
            mode={modalMode}
            onClose={() => setModalOpen(false)}
            onStartWallet={() => startWalletAuth()}
          />
        </>
      )}
    </Box>
  );
}

export default memo(AuthButtons);
